import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../components/ui/AppButton";
import { computePresetGrantSet, diffPresetApplication } from "../../utils/authorityPresets";

const UMAuthorityProfilePicker = ({ presets, catalog, grants, activePresetId, applyingPresetId, disabled, onApplyPreset }) => {
  const { t } = useTranslation(["staff", "common"]);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { preset, toGrant, toRevoke }

  const handleCardClick = (preset) => {
    if (disabled || applyingPresetId) return;
    const { toGrant, toRevoke } = diffPresetApplication(preset, catalog, grants);
    if (toGrant.length === 0 && toRevoke.length === 0) return;

    if (toRevoke.length > 0) {
      setPendingConfirm({ preset, toGrant, toRevoke });
    } else {
      onApplyPreset(preset);
    }
  };

  const confirmApply = () => {
    if (!pendingConfirm) return;
    onApplyPreset(pendingConfirm.preset);
    setPendingConfirm(null);
  };

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body">
        <h6 className="fw-bold mb-1">{t("staff:profiles.title")}</h6>
        <p className="text-muted small mb-3">{t("staff:profiles.subtitle")}</p>

        <div className="authority-profile-grid">
          {presets.map((preset) => {
            const grantCount = computePresetGrantSet(preset, catalog).size;
            const isActive = activePresetId === preset.id;
            const isApplying = applyingPresetId === preset.id;

            return (
              <div
                key={preset.id}
                className={`authority-profile-card ${isActive ? "active" : ""}`}
                role="button"
                onClick={() => handleCardClick(preset)}
              >
                <div className="authority-profile-icon">
                  {isApplying ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <i className={`fas ${preset.icon}`}></i>
                  )}
                </div>
                <div className="authority-profile-name">
                  {t(`staff:profiles.${preset.translationKey}.name`)}
                  {isActive && <i className="fas fa-circle-check text-success ms-2"></i>}
                </div>
                <div className="authority-profile-desc">{t(`staff:profiles.${preset.translationKey}.description`)}</div>
                <div className="authority-profile-count">
                  {t("staff:profiles.grantCount", { count: grantCount })}
                </div>
              </div>
            );
          })}

          {activePresetId === null && (
            <div className="authority-profile-card custom-active">
              <div className="authority-profile-icon">
                <i className="fas fa-sliders"></i>
              </div>
              <div className="authority-profile-name">{t("staff:profiles.customBadge")}</div>
              <div className="authority-profile-desc">{t("staff:profiles.customDescription")}</div>
            </div>
          )}
        </div>

        <p className="text-muted small mb-0 mt-3">
          <i className="fas fa-circle-info me-1"></i>
          {t("staff:profiles.clubAccessNote")}
        </p>
      </div>

      {pendingConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title">{t("staff:profiles.applyConfirm.title")}</h5>
                <button type="button" className="btn-close" onClick={() => setPendingConfirm(null)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  {t("staff:profiles.applyConfirm.body", {
                    grantCount: pendingConfirm.toGrant.length,
                    revokeCount: pendingConfirm.toRevoke.length,
                  })}
                </p>
              </div>
              <div className="modal-footer">
                <AppButton variant="secondary" type="button" onClick={() => setPendingConfirm(null)}>
                  {t("common:buttons.cancel")}
                </AppButton>
                <AppButton variant="primary" type="button" onClick={confirmApply}>
                  {t("staff:profiles.applyConfirm.confirm")}
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UMAuthorityProfilePicker;
